import { useEffect, useState } from "react";
import { PageHeader } from "@/components/PageHeader";
import { DataTable } from "@/components/DataTable";
import { LoadingState, ErrorState } from "@/components/LoadingError";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { apiFetch } from "@/utils/api";

interface UserRow {
  id: string;
  username: string;
  display_name: string;
  role_id?: string;
  role_label: string;
  active: number;
  created_at: string;
}

interface Role {
  id: string;
  label: string;
}

export function AdminUsersPage() {
  const [pending, setPending] = useState<UserRow[]>([]);
  const [active, setActive] = useState<UserRow[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [newUser, setNewUser] = useState({ username: "", password: "", displayName: "", roleId: "role_tenant_user" });

  const load = async () => {
    const [users, rolesData] = await Promise.all([
      apiFetch<{ pending: UserRow[]; active: UserRow[] }>("/admin/users"),
      apiFetch<{ roles: Role[] }>("/admin/roles"),
    ]);
    setPending(users.pending);
    setActive(users.active);
    setRoles(rolesData.roles);
  };

  useEffect(() => {
    load()
      .catch((e) => setError(e instanceof Error ? e.message : "Failed to load"))
      .finally(() => setLoading(false));
  }, []);

  const approve = async (id: string) => {
    await apiFetch(`/admin/users/${id}/approve`, { method: "POST" });
    await load();
  };

  const changeRole = async (id: string, roleId: string) => {
    await apiFetch(`/admin/users/${id}/role`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ roleId }),
    });
    await load();
  };

  const createUser = async () => {
    await apiFetch("/admin/users", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(newUser),
    });
    setShowNew(false);
    setNewUser({ username: "", password: "", displayName: "", roleId: "role_tenant_user" });
    await load();
  };

  if (loading) return <LoadingState />;
  if (error) return <ErrorState message={error} />;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Users"
        actions={
          <Button onClick={() => setShowNew(!showNew)} className="bg-[#1e3a4f] hover:bg-[#163041]">
            {showNew ? "Cancel" : "Add User"}
          </Button>
        }
      />

      {showNew && (
        <div className="bg-white rounded-lg shadow p-4 grid gap-3 sm:grid-cols-2 max-w-xl">
          <div className="space-y-1.5">
            <Label>Username</Label>
            <Input value={newUser.username} onChange={(e) => setNewUser({ ...newUser, username: e.target.value })} />
          </div>
          <div className="space-y-1.5">
            <Label>Display Name</Label>
            <Input value={newUser.displayName} onChange={(e) => setNewUser({ ...newUser, displayName: e.target.value })} />
          </div>
          <div className="space-y-1.5">
            <Label>Password</Label>
            <Input type="password" value={newUser.password} onChange={(e) => setNewUser({ ...newUser, password: e.target.value })} />
          </div>
          <div className="space-y-1.5">
            <Label>Role</Label>
            <Select value={newUser.roleId} onChange={(e) => setNewUser({ ...newUser, roleId: e.target.value })}>
              {roles.map((r) => (
                <option key={r.id} value={r.id}>{r.label}</option>
              ))}
            </Select>
          </div>
          <Button onClick={createUser} className="sm:col-span-2 w-fit bg-[#1e3a4f] hover:bg-[#163041]">
            Create User
          </Button>
        </div>
      )}

      {pending.length > 0 && (
        <section className="space-y-2">
          <h3 className="font-semibold text-[#1e3a4f]">Pending Approval</h3>
          <DataTable
            columns={[
              { key: "user", header: "Username", render: (u) => u.username },
              { key: "name", header: "Display Name", render: (u) => u.display_name },
              { key: "role", header: "Role", render: (u) => u.role_label },
              {
                key: "action",
                header: "Action",
                render: (u) => (
                  <Button size="sm" onClick={() => approve(u.id)}>Approve</Button>
                ),
              },
            ]}
            rows={pending}
            rowKey={(u) => u.id}
          />
        </section>
      )}

      <section className="space-y-2">
        <h3 className="font-semibold text-[#1e3a4f]">Active Users</h3>
        <DataTable
          columns={[
            { key: "user", header: "Username", render: (u) => u.username },
            { key: "name", header: "Display Name", render: (u) => u.display_name },
            {
              key: "role",
              header: "Role",
              render: (u) => (
                <Select
                  value={u.role_id ?? ""}
                  onChange={(e) => changeRole(u.id, e.target.value)}
                  className="max-w-40"
                >
                  {roles.map((r) => (
                    <option key={r.id} value={r.id}>{r.label}</option>
                  ))}
                </Select>
              ),
            },
          ]}
          rows={active}
          rowKey={(u) => u.id}
        />
      </section>
    </div>
  );
}
